"use client";

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { HASHTAG_ONLY, RICH_TEXT_SPLIT, hashtagHref } from "@/lib/hashtag-links";
import { caretAfterEdit, matchesMention, mentionFragment } from "@/lib/mention-caret";

interface MentionUser {
  id: string;
  username: string;
  displayName: string;
  image: string | null;
}

export interface MentionContext {
  type: "study-group" | "forum-topic" | "video";
  id: string;
}

interface MentionInputProps {
  value: string;
  onChange: (val: string) => void;
  /** Fired with the caret after each edit, so composers can run other autocomplete (hashtags). */
  onCaretChange?: (val: string, cursor: number) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  mentionContext?: MentionContext;
  /** Multi-line composers (feed posts, discussions, forum topics). */
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  /** Grow with content. Used by the single-row feed composer. */
  autoResize?: boolean;
  /** Comment fields open upward. Composers near the top of the page open downward. */
  menuPlacement?: "above" | "below";
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
}

type MenuPos = { top: number; left: number; width: number; maxHeight: number };

/**
 * A text field that shows a @ mention dropdown when the user types
 * "@sometext". Selecting a suggestion inserts "@username " into the value.
 */
function assignFieldRef(
  ref: React.Ref<HTMLInputElement | HTMLTextAreaElement> | undefined,
  node: HTMLInputElement | HTMLTextAreaElement | null,
) {
  if (!ref) return;
  if (typeof ref === "function") ref(node);
  else (ref as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>).current = node;
}

function placeMenu(rect: DOMRect, placement: "above" | "below"): MenuPos {
  const gap = 4;
  const width = Math.min(320, Math.max(256, rect.width));
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - gap);
  const spaceAbove = Math.max(0, rect.top - gap);
  const wantBelow = placement === "below";
  const openBelow = wantBelow ? spaceBelow >= spaceAbove : spaceAbove < spaceBelow;
  const available = openBelow ? spaceBelow : spaceAbove;
  const maxHeight = Math.min(256, Math.max(120, available));
  const top = openBelow
    ? Math.min(rect.bottom + gap, Math.max(8, window.innerHeight - maxHeight - 8))
    : Math.max(8, rect.top - gap - maxHeight);
  return { top, left, width, maxHeight };
}

export function MentionInput({
  value,
  onChange,
  onCaretChange,
  onKeyDown,
  id,
  placeholder,
  disabled,
  className,
  autoFocus,
  mentionContext,
  multiline = false,
  rows,
  maxLength,
  autoResize = false,
  menuPlacement = "above",
  inputRef: inputRefProp,
}: MentionInputProps) {
  const [suggestions, setSuggestions] = useState<MentionUser[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);
  const valueRef = useRef(value);
  const queryRef = useRef<string | null>(null);
  /** People returned for "@" alone, so the next letters can filter before the network answers. */
  const directoryRef = useRef<MentionUser[]>([]);
  const suggestionsRef = useRef<MentionUser[]>([]);

  const contextKey = mentionContext ? `${mentionContext.type}:${mentionContext.id}` : "";
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    directoryRef.current = [];
    suggestionsRef.current = [];
    seqRef.current += 1;
    setSuggestions([]);
    setOpen(false);
  }, [contextKey]);

  const applyUsers = useCallback((users: MentionUser[]) => {
    suggestionsRef.current = users;
    setSuggestions(users);
    setActiveIdx(0);
    setOpen(users.length > 0);
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    const seq = ++seqRef.current;
    const startedContext = contextKeyRef.current;
    try {
      const params = new URLSearchParams({ q });
      if (mentionContext) {
        params.set("context", mentionContext.type);
        params.set("contextId", mentionContext.id);
      }
      const res = await fetch(`/api/users/mention?${params}`);
      const data = await res.json();
      if (startedContext !== contextKeyRef.current) return;
      const users = (data.users || []) as MentionUser[];
      if (q === "") directoryRef.current = users;
      if (seq !== seqRef.current) {
        const current = queryRef.current;
        if (q === "" && current) {
          const local = users.filter((user) => matchesMention(user, current));
          if (local.length > 0) applyUsers(local);
        }
        return;
      }
      applyUsers(users);
    } catch {
      if (seq !== seqRef.current) return;
      const current = queryRef.current;
      const local = current
        ? directoryRef.current.filter((user) => matchesMention(user, current))
        : [];
      applyUsers(local);
    }
  }, [applyUsers, mentionContext]);

  useEffect(() => {
    if (!autoResize || !multiline) return;
    const el = inputRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    if (start != null && end != null) {
      try {
        el.setSelectionRange(start, end);
      } catch {
        // Some browsers reject setSelectionRange while the field is updating.
      }
    }
  }, [value, autoResize, multiline]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const previous = valueRef.current;
    const newVal = e.target.value;
    const cursor = caretAfterEdit(previous, newVal, e.target.selectionStart);
    valueRef.current = newVal;
    onChange(newVal);
    onCaretChange?.(newVal, cursor);

    const fragment = mentionFragment(newVal, cursor);
    queryRef.current = fragment;

    if (fragment !== null) {
      setActiveIdx(0);
      const pool = directoryRef.current.length > 0 ? directoryRef.current : suggestionsRef.current;
      const local = pool.filter((user) => matchesMention(user, fragment));
      // Narrow immediately. An empty local pass waits for the server so a
      // letter doesn't wipe the menu before matches come back.
      if (local.length > 0) applyUsers(local);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchSuggestions(fragment), 180);
    } else {
      suggestionsRef.current = [];
      setSuggestions([]);
      setOpen(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      seqRef.current += 1;
    }
  };

  const insertMention = (user: MentionUser) => {
    const current = valueRef.current;
    let cursor = inputRef.current?.selectionStart ?? current.length;
    if (mentionFragment(current, cursor) === null && mentionFragment(current, current.length) !== null) {
      cursor = current.length;
    }
    const before = current.slice(0, cursor);
    const after = current.slice(cursor);
    const replaced = before.replace(/@([A-Za-z0-9_.-]*)$/, `@${user.username} `);
    const next = replaced + after;
    valueRef.current = next;
    onChange(next);
    queryRef.current = null;
    suggestionsRef.current = [];
    setOpen(false);
    setSuggestions([]);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      const pos = replaced.length;
      inputRef.current?.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (open && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        if (suggestions[activeIdx]) {
          e.preventDefault();
          insertMention(suggestions[activeIdx]);
          return;
        }
      }
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
    }
    onKeyDown?.(e);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = inputRef.current;
      if (!el) return;
      setMenuPos(placeMenu(el.getBoundingClientRect(), menuPlacement));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, menuPlacement, suggestions.length, value]);

  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const node = menu?.querySelector<HTMLElement>(`[data-mention-index="${activeIdx}"]`);
    if (!menu || !node) return;
    const top = node.offsetTop;
    const bottom = top + node.offsetHeight;
    if (top < menu.scrollTop) menu.scrollTop = top;
    else if (bottom > menu.scrollTop + menu.clientHeight) menu.scrollTop = bottom - menu.clientHeight;
  }, [activeIdx, open, suggestions]);

  const menu =
    open && suggestions.length > 0 && menuPos && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={menuRef}
            className="fixed z-[220] overflow-y-auto overscroll-contain bg-white border border-gray-200 rounded-lg shadow-lg text-sm"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              overflowY: "auto",
            }}
            role="listbox"
            aria-label="Mention a member"
          >
            {suggestions.map((u, i) => (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected={i === activeIdx}
                data-mention-index={i}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(u);
                }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
                  i === activeIdx ? "bg-blue-50 text-blue-700" : "text-gray-800"
                }`}
              >
                {u.image ? (
                  <img
                    src={u.image}
                    alt=""
                    className="h-6 w-6 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <span className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0 uppercase">
                    {(u.displayName || u.username).charAt(0)}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block font-medium truncate leading-tight">{u.displayName}</span>
                  <span className="block text-xs text-gray-400 truncate leading-tight">@{u.username}</span>
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={containerRef} className="relative flex-1">
      {multiline ? (
        <textarea
          ref={(node) => {
            inputRef.current = node;
            assignFieldRef(inputRefProp, node);
          }}
          id={id}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          rows={rows ?? 3}
          maxLength={maxLength}
          className={
            className
              ? `w-full ${className}`
              : "w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          }
        />
      ) : (
        <input
          ref={(node) => {
            inputRef.current = node;
            assignFieldRef(inputRefProp, node);
          }}
          id={id}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          maxLength={maxLength}
          className={
            className
              ? `w-full ${className}`
              : "w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          }
        />
      )}
      {menu}
    </div>
  );
}

/**
 * Renders text content and turns @username tokens into clickable profile links.
 */
export function MentionText({ text, className }: { text: string; className?: string }) {
  const parts = text.split(RICH_TEXT_SPLIT);
  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (/^@[a-zA-Z0-9_-]+$/.test(part)) {
          const username = part.slice(1);
          return (
            <a
              key={i}
              href={`/social-profile/${encodeURIComponent(username)}`}
              className="text-blue-600 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        if (HASHTAG_ONLY.test(part)) {
          return (
            <a
              key={i}
              href={hashtagHref(part)}
              className="text-blue-600 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </span>
  );
}
